<script>
import dayjs from 'dayjs';
import {
  pageTitleMixin,
  prefetchDataMixin,
} from '@ubic/vue-mixins';
import { getRandomString } from '@/utils';
import { permissions as permissionsEnum, permissionTitles } from '@/constants';
import ErrorAccessDenied from '@/components/ErrorAccessDenied';
import DeleteWithConfirm from '@/components/DeleteWithConfirm';
import FormError from '@/components/FormError';
import service from './service';

const tomorrow = dayjs().add(1, 'd').format('YYYY-MM-DD');

export default {
  pageTitle: 'Детали администратора',
  async prefetchData () {
    if (!this.hasReadPermission) {
      return;
    }

    this.isFetching = true;

    this.admin = await service.getById(this.$route.params.id);

    this.isFetching = false;
    this.username = this.admin.username;
    this.isBot = Boolean(this.admin.isBot);
    this.permissions = this.admin.permissions;
  },
  mixins: [
    pageTitleMixin,
    prefetchDataMixin,
  ],
  data () {
    return {
      admin: {},
      username: '',
      isBot: false,
      password: '',
      permissions: [],
      isFetching: false,
      isSaving: false,
      isPasswordSaving: false,
      error: '',

      tokenExpiresOn: tomorrow,
      isTokenGenerating: false,
      generatedToken: '',
    };
  },
  computed: {
    hasReadPermission () {
      return this.$store
        .getters['auth/checkPermission'](permissionsEnum.ADMIN_READ);
    },
    hasWritePermission () {
      return this.$store
        .getters['auth/checkPermission'](permissionsEnum.ADMIN_WRITE);
    },
    isReadonly () {
      return !this.hasWritePermission;
    },
  },
  methods: {
    handleUsernameInput (value) {
      this.username = value;
    },
    handleIsBotChange (value) {
      this.isBot = value;
    },
    handlePasswordInput (value) {
      this.password = value;
    },
    handleGeneratePassword () {
      this.password = getRandomString() + getRandomString();

      this.$nextTick(() => {
        document.getElementById('passwordInput').select();
      });
    },
    handlePermissionsInput (values) {
      this.permissions = values;
    },
    async handleSubmit (e) {
      e.preventDefault();

      if (!this.hasWritePermission || this.isSaving) {
        return;
      }

      this.isSaving = true;
      this.error = '';

      try {
        const { username, isBot, permissions } = this;
        const { id } = this.$route.params;

        await service.update(id, { username, isBot, permissions });
        this.emitDataUpdate();
        this.handleClose();
      } catch (err) {
        this.error = 'Ошибка при сохранении пользователя';
      }

      this.isSaving = false;
    },
    async handlePasswordSubmit (e) {
      e.preventDefault();

      if (!this.hasWritePermission || this.isPasswordSaving) {
        return;
      }

      this.isPasswordSaving = true;
      this.error = '';

      try {
        const { password } = this;
        const { id } = this.$route.params;

        await service.update(id, { password });
      } catch (err) {
        this.error = 'Не удалось изменить пароль';
      }

      this.password = '';
      this.isPasswordSaving = false;
    },
    async handleDelete () {
      if (!this.hasWritePermission) {
        return;
      }

      this.isFetching = true;

      try {
        await service.remove(this.$route.params.id);

        this.emitDataUpdate();
        this.handleClose();
      } catch (err) {
        this.isFetching = false;
        this.error = 'Ошибка при удалении администратора';
      }
    },

    handleTokenExpiresOnChange (value) {
      this.tokenExpiresOn = value;
    },
    handleTokenValueInputFocus (e) {
      e.target.select();
    },
    async handleTokenSubmit (e) {
      e.preventDefault();

      if (this.isTokenGenerating) {
        return;
      }

      this.isTokenGenerating = true;

      try {
        const token = await service.generateToken(
          this.$route.params.id,
          { expiresOn: this.tokenExpiresOn },
        );

        this.generatedToken = token;
      } catch (error) {
        this.error = 'Не удалось сгенерировать токен';
      }

      this.isTokenGenerating = false;
    },

    handleClose () {
      this.$emit('close');
      this.$router.replace('/admins');
    },
    emitDataUpdate () {
      this.$emit('dataupdate');
    },
  },
  render () {
    if (!this.hasReadPermission) {
      return (
        <u-modal onClose={this.handleClose}>
          <ErrorAccessDenied />
        </u-modal>
      );
    }

    const {
      $style,
      admin,
      username,
      isBot,
      password,
      permissions,
      isSaving,
      isPasswordSaving,
      error,
      isReadonly,
      handlePermissionsInput,
    } = this;

    return (
      <u-modal onClose={this.handleClose}>
        <u-title
          slot="header"
          level="2"
        >
          <span>
            Администратор
            &nbsp;
          </span>
          <span slot="sub">
            {admin.username}
          </span>
        </u-title>

        <form
          action="#"
          method="post"
          onSubmit={this.handleSubmit}
        >
          <div class="form-field">
            <div class="form-field__label">
              <label for="usernameInput">
                Логин
              </label>
            </div>
            <u-input
              id="usernameInput"
              name="username"
              required
              fullwidth
              static={isReadonly}
              value={username}
              onInput={this.handleUsernameInput}
            />
          </div>
          <div class="form-field">
            <u-checkbox
              name="isBot"
              value="isBot"
              disabled={isReadonly}
              checked={isBot}
              onChange={this.handleIsBotChange}
            >
              является ботом
            </u-checkbox>
          </div>
          <div class="form-field">
            <div class="form-field__label">
              Полномочия
            </div>
            <div>
              {Object
                .values(permissionsEnum)
                .map(permission => (
                  <div
                    key={permission}
                    class={$style.checkbox}
                  >
                    <u-checkbox
                      name="permissions"
                      disabled={isReadonly}
                      value={permission}
                      checked={permissions}
                      onChange={handlePermissionsInput}
                    >
                      {permissionTitles[permission]}
                    </u-checkbox>
                  </div>
                ))}
            </div>
          </div>
          {Boolean(error) && (
            <FormError>
              {error}
            </FormError>
          )}
          {!isReadonly && (
            <div class="form-footer">
              <u-btn
                type="submit"
                size="wide"
              >
                {isSaving ? (
                  <u-spinner inline />
                ) : 'Сохранить'}
              </u-btn>
              <span class="m-l-auto">
                <DeleteWithConfirm onConfirm={this.handleDelete} />
              </span>
            </div>
          )}
        </form>

        {(!isReadonly && !isBot) && (
          <form
            action="#"
            method="post"
            onSubmit={this.handlePasswordSubmit}
          >
            <u-title level="3">
              Изменение пароля
            </u-title>

            <div>
              <span class={$style.changePassword}>
                <u-input
                  id="passwordInput"
                  name="password"
                  required
                  placeholder="Введите новый пароль"
                  value={password}
                  onInput={this.handlePasswordInput}
                />

                <button
                  class={$style.inputButton}
                  type="button"
                  onClick={this.handleGeneratePassword}
                >
                  сгенерировать
                </button>

                <u-btn
                  class={$style.changePasswordSubmit}
                  type="submit"
                  appearance="outline"
                >
                  {isPasswordSaving ? (
                    <u-spinner inline />
                  ) : 'Изменить'}
                </u-btn>
              </span>
            </div>
          </form>
        )}
        {(!isReadonly && isBot) && (
          <form
            action="#"
            method="post"
            onSubmit={this.handleTokenSubmit}
          >
            <u-title level="3">
              Генерация токена
            </u-title>

            <div>
              <span class={$style.changePassword}>
                <span class={$style.changePasswordLabel}>
                  Активен до
                </span>
                <u-datepicker
                  required
                  min={tomorrow}
                  value={this.tokenExpiresOn}
                  onInput={this.handleTokenExpiresOnChange}
                />

                <u-btn
                  class={$style.changePasswordSubmit}
                  type="submit"
                  appearance="outline"
                >
                  {this.isTokenGenerating ? (
                    <u-spinner inline />
                  ) : 'Сгенерировать'}
                </u-btn>
              </span>
            </div>

            {Boolean(this.generatedToken) && (
              <div>
                <u-textarea
                  class={$style.tokenTextarea}
                  rows="1"
                  value={this.generatedToken}
                  readonly
                  onFocus={this.handleTokenValueInputFocus}
                />
              </div>
            )}
          </form>
        )}
      </u-modal>
    );
  },
};
</script>

<style lang="scss" module>
@import '@/theme/include.scss';

.inputButton {
  @include button-reset();

  color: $color-primary;
  font-size: .75em;
  margin-left: 1em;

  &:hover,
  &:focus {
    color: $color-primary-lighter;
  }
}

.checkbox {
  & + & {
    margin-top: .5em;
  }
}

.changePassword {
  align-items: center;
  display: flex;

  &Label {
    margin-right: .5em;
  }

  &Submit {
    margin-left: auto;
  }
}

.tokenTextarea {
  margin-top: 1em;
  min-height: 80px;
  resize: none;
}
</style>
